import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '../../services/api';
import SquareCardEntry from './SquareCardEntry';
import AddressAutocompleteInput from './AddressAutocompleteInput';
import { detectCardType } from '../../utils/cardBrand';
import CardNumberInput from './CardNumberInput';
import '../AllCss/CardNumberInput.css';

/**
 * PCI-safe add-card panel for Profile (Square tokenization or dev mock).
 */
export default function AddPaymentCardPanel({
  cardholderNameDefault = '',
  billingDefaults = {},
  useSameAddress,
  onUseSameAddressChange,
  personalInfo,
  onCancel,
  onSuccess,
  onError,
  loading,
}) {
  const [squareConfig, setSquareConfig] = useState(null);
  const [loadingSquare, setLoadingSquare] = useState(true);
  const [nickname, setNickname] = useState('');
  const [cardholderName, setCardholderName] = useState(cardholderNameDefault);
  const [cardAddress, setCardAddress] = useState(billingDefaults);
  const [mockCard, setMockCard] = useState({ cardNumber: '', expiryDate: '', cvv: '' });

  useEffect(() => {
    setCardholderName(cardholderNameDefault);
  }, [cardholderNameDefault]);

  useEffect(() => {
    if (useSameAddress && personalInfo) {
      setCardAddress({
        address: personalInfo.address || '',
        address2: personalInfo.address2 || '',
        city: personalInfo.city || '',
        state: personalInfo.state || '',
        countyName: personalInfo.countyName || '',
        country: 'United States',
        zipCode: personalInfo.zipCode || '',
      });
    }
  }, [useSameAddress, personalInfo]);

  useEffect(() => {
    api
      .get('/payment-cards/square-config')
      .then((res) => {
        if (res.data.success) setSquareConfig(res.data.data);
      })
      .catch(() => setSquareConfig(null))
      .finally(() => setLoadingSquare(false));
  }, []);

  const billingPayload = () => ({
    nickname: nickname || null,
    cardholderName: cardholderName.trim(),
    address: cardAddress.address || null,
    address2: cardAddress.address2 || null,
    city: cardAddress.city || null,
    state: cardAddress.state || null,
    countyName: cardAddress.countyName || null,
    country: cardAddress.country || 'United States',
    zipCode: cardAddress.zipCode || null,
    zipcode: cardAddress.zipCode || null,
  });

  const saveWithToken = async (squareSourceId) => {
    try {
      const response = await api.post('/payment-cards', {
        ...billingPayload(),
        squareSourceId,
        isDefault: false,
      });
      if (response.data.success) {
        onSuccess(response.data.message);
      }
    } catch (err) {
      onError(err.response?.data?.message || 'Failed to add payment card');
    }
  };

  const saveMock = async (e) => {
    e.preventDefault();
    const parts = mockCard.expiryDate.split('/');
    if (parts.length !== 2) {
      onError('Expiry must be MM/YY');
      return;
    }
    try {
      const response = await api.post('/payment-cards', {
        ...billingPayload(),
        mockCard: true,
        cardLast4: mockCard.cardNumber.replace(/\D/g, '').slice(-4) || '4242',
        cardType: detectCardType(mockCard.cardNumber),
        expiryMonth: parseInt(parts[0], 10),
        expiryYear: parseInt(`20${parts[1]}`, 10),
      });
      if (response.data.success) onSuccess(response.data.message);
    } catch (err) {
      onError(err.response?.data?.message || 'Failed to add payment card');
    }
  };

  const useSquare = squareConfig?.inlinePaymentsEnabled;
  const useMock = squareConfig?.squareMock && !useSquare;
  const mockCardBrand = detectCardType(mockCard.cardNumber);

  return (
    <div className="mt-6 p-6 bg-neutral-50 border border-neutral-200">
      <h3 className="text-sm font-semibold text-neutral-900 mb-6">Add New Card</h3>
      <p className="text-xs text-neutral-600 mb-4">
        Cards are stored securely with Square. We never save your full card number or security code.
      </p>

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
            Nickname of card
          </label>
          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-neutral-300"
            placeholder="e.g., My Primary Card"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
            Cardholder Name
          </label>
          <input
            type="text"
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-neutral-300"
          />
        </div>

        <div className="pt-4 border-t border-neutral-200">
          <label className="flex items-center gap-2 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={useSameAddress}
              onChange={(e) => onUseSameAddressChange(e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-xs font-medium text-neutral-700 uppercase tracking-wide">
              Use same address as personal info
            </span>
          </label>
          {!useSameAddress && (
            <AddressAutocompleteInput
              id="profile-add-card-address"
              label="Billing address"
              value={cardAddress}
              onChange={(patch) => setCardAddress((prev) => ({ ...prev, ...patch }))}
              labelClassName="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2"
              inputClassName="w-full px-3 py-2 text-sm border border-neutral-300"
              placeholder="Start typing your billing address…"
            />
          )}
        </div>

        {loadingSquare && (
          <p className="text-sm text-neutral-600 flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading secure form…
          </p>
        )}

        {useSquare && !loadingSquare && (
          <SquareCardEntry
            applicationId={squareConfig.applicationId}
            locationId={squareConfig.locationId}
            submitLabel="Add card"
            submittingLabel="Adding…"
            disabled={loading}
            onToken={saveWithToken}
            onError={onError}
            buttonClassName="px-6 py-2 bg-neutral-900 text-white text-sm font-medium disabled:opacity-50"
          />
        )}

        {useMock && !loadingSquare && (
          <form onSubmit={saveMock} className="space-y-4">
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 p-2 rounded">
              Dev mock mode — test cards only.
            </p>
            <div>
              <label className="block text-xs font-medium text-neutral-700 uppercase mb-2">
                Card number
              </label>
              <CardNumberInput
                id="profile-add-card-number"
                value={mockCard.cardNumber}
                onChange={(e) =>
                  setMockCard({ ...mockCard, cardNumber: e.target.value })
                }
                placeholder="3782 822463 10005"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="MM/YY"
                value={mockCard.expiryDate}
                onChange={(e) => setMockCard({ ...mockCard, expiryDate: e.target.value })}
                className="px-3 py-2 text-sm border border-neutral-300"
              />
              <input
                type="text"
                placeholder="CVV"
                value={mockCard.cvv}
                onChange={(e) => setMockCard({ ...mockCard, cvv: e.target.value })}
                className="px-3 py-2 text-sm border border-neutral-300"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-neutral-900 text-white text-sm font-medium disabled:opacity-50"
            >
              {loading ? 'Adding…' : 'Add card'}
            </button>
          </form>
        )}

        {!useSquare && !useMock && !loadingSquare && (
          <p className="text-sm text-red-700">
            Secure card entry is not configured. Set SQUARE_APPLICATION_ID and SQUARE_LOCATION_ID on the server.
          </p>
        )}

        <button type="button" onClick={onCancel} className="px-6 py-2 border border-neutral-300 text-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}
